<script setup lang="ts">
  import {
    TableLoading,
    Pagination,
    SelectInput,
    ColoredButton,
    Checkbox,
    FieldSearchBar,
    SearchNoResults,
    SearchToolbar,
    LoadErrorState,
    NoDocuments,
    DefaultTable,
    TableHeader,
    DocumentRevisionStatusDot,
  } from '@/shared/ui';
  import QualityRecordsActionButtons from './QualityRecordsActionButtons.vue';
  import QualityRecordRowActions from './QualityRecordRowActions.vue';
  import RecordTypeBadge from './RecordTypeBadge.vue';
  import type { FilterOptions } from '@/entities/Document';
  import { RECORD_TYPE_UPLOAD_NAME } from '@/entities/GeneratedRecord';
  import type { EntityType, OrderOption, SortSelectionSource } from '@/shared/lib';
  import { QUANTITY_OPTIONS } from '@/shared/config';
  import {
    getQualityRecordStatusDotColorClass,
    QUALITY_RECORD_STAGE_BADGE_CLASS,
    getQualityRecordStatusLabel,
    GENERATED_RECORDS_LOAD_ERROR_TITLE,
  } from '@/pages/home/config';
  import { ORDER_OPTIONS } from '@/entities/Document';
  import NoFilesIcon from '@/shared/assets/images/no-files.svg';
  import RegistrationPaperIcon from '@/shared/assets/icons/RegistrationPaper.svg';
  import MenuFriesLeftIcon from '@/shared/assets/icons/MenuFriesLeftIcon.svg';
  import { useQualityRecordSelection } from '../../model/useQualityRecordSelection';
  import { computed, type Component } from 'vue';
  import { QualityRecordFilterModal } from '../';
  import { useTableFilterModal } from '@/shared/lib/use';
  import { useFilterStore } from '@/entities/Filter';
  import type { QualityRecordRow } from '../../model/quality-record-row.types';
  import type { GeneratedRecordFilters } from '../../model/generated-record-filters';
  import {
    DEFAULT_QUALITY_RECORD_SEARCH_FIELD,
    QUALITY_RECORD_SEARCH_FIELDS,
    type QualityRecordSearchField,
  } from '../../model/quality-records-const';

  const props = withDefaults(defineProps<{
    documents: QualityRecordRow[];
    loading: boolean;
    title?: string;
    totalItems: number;
    currentPage: number;
    itemsPerPage: number;
    searchTerm?: string;
    currentFilter?: FilterOptions | GeneratedRecordFilters;
    error?: boolean;
    emptyTitle?: string;
    paginationEntity?: EntityType;
    disablePendingActions?: boolean;
    /** Turns Filtrar on while `disablePendingActions` keeps the other actions off. */
    enableFilter?: boolean;
    /** Makes an active filter count like a search term: a filtered empty list keeps the buttons. */
    filterAwareEmptyState?: boolean;
    filterModal?: Component;
    filterModalProps?: Record<string, unknown>;
    /** Turns Ordenar por on while `disablePendingActions` keeps the other actions off. */
    enableSort?: boolean;
    orderOptions?: readonly OrderOption[];
    sortSource?: SortSelectionSource | null;
  }>(), {
    title: undefined,
    searchTerm: undefined,
    currentFilter: undefined,
    error: false,
    emptyTitle: 'Ops! Parece que a pasta ainda não contém documentos',
    paginationEntity: 'documento',
    disablePendingActions: false,
    enableFilter: false,
    filterAwareEmptyState: false,
    filterModal: undefined,
    filterModalProps: undefined,
    enableSort: false,
    orderOptions: () => ORDER_OPTIONS,
    sortSource: null,
  });

  const emit = defineEmits([
    'search',
    'page-changed',
    'quantity-changed',
    'master-list',
    'report',
    'update:sort',
    'update:filter',
    'filter-clicked',
    'records-selected',
    'retry',
    'refresh',
  ]);

  const {
    hasSelectedRecords,
    isRecordSelected,
    toggleRecordSelection,
    selectedRecords,
    deselectAllRecords,
  } = useQualityRecordSelection();

  const searchFieldId = defineModel<QualityRecordSearchField>('searchFieldId', {
    default: DEFAULT_QUALITY_RECORD_SEARCH_FIELD,
  });

  const handleToggleSelection = (id: number) => {
    toggleRecordSelection(id);
    emit('records-selected', selectedRecords.value);
  };

  const handleToggleAll = () => {
    const allCurrentlySelected = props.documents.every((doc) => isRecordSelected(doc.id));
    if (allCurrentlySelected) {
      deselectAllRecords();
    } else {
      props.documents.forEach((doc) => {
        if (!isRecordSelected(doc.id)) {
          toggleRecordSelection(doc.id);
        }
      });
    }
    emit('records-selected', selectedRecords.value);
  };

  const allSelected = computed(
    () => props.documents.length > 0 && props.documents.every((doc) => isRecordSelected(doc.id))
  );

  type TableFilters = FilterOptions | GeneratedRecordFilters;

  const handleFilter = (filters: TableFilters) => {
    emit('update:filter', filters);
  };

  const filterStore = useFilterStore();
  const { isFilterActive, openFilterModal } = useTableFilterModal<TableFilters>(
    props.filterModal ?? QualityRecordFilterModal,
    filterStore,
    handleFilter,
    () => props.currentFilter || {},
    () => props.filterModalProps ?? {}
  );

  const filterDisabled = computed(() => props.disablePendingActions && !props.enableFilter);

  const handleFilterClicked = () => {
    openFilterModal();
  };

  const hasResults = computed(() => props.documents && props.documents.length > 0);
  const hasQuery = computed(
    () => Boolean(props.searchTerm) || (props.filterAwareEmptyState && isFilterActive.value)
  );
  const shouldShowNoResults = computed(
    () => !hasResults.value && hasQuery.value && !props.loading && !props.error
  );
  const shouldShowEmptyState = computed(
    () => !hasResults.value && !props.loading && !props.searchTerm && !props.error
  );

  // The column draws the prefix alone, which is the field the "Registros gerados" scope searches.
  // Concatenating the record name put text on the screen that no scope can find. The `Registro {id}`
  // fallback is not searchable either, and stays only because a row with no prefix would otherwise
  // render an empty cell.
  const formatRecordName = (record: QualityRecordRow) =>
    record.prefix.trim() === '' ? `Registro ${record.id}` : record.prefix;

  const isEmUsoQualityRecord = (record: QualityRecordRow) => record.status === 'EM_USO';

  // An Upload record is never locked, so its availability dot would say nothing.
  const showsStatusDot = (record: QualityRecordRow) =>
    record.recordTypeName !== RECORD_TYPE_UPLOAD_NAME;

  const getEmUsoTooltip = (record: QualityRecordRow) =>
    `Em uso por ${record.filledBy ?? 'Usuário não informado'}`;

  const getStatusTooltip = (record: QualityRecordRow) =>
    isEmUsoQualityRecord(record)
      ? getEmUsoTooltip(record)
      : getQualityRecordStatusLabel(record.status);

  const HEADER_CLASS =
    'text-left text-xs/5 font-semibold text-gray-07 pl-6 py-3.5 whitespace-nowrap align-middle';
  // The code column takes whatever width is left, so the fixed columns after it end at the right
  // edge of the card, as in the design.
  const CODE_HEADER_CLASS =
    'w-full text-left text-xs/5 font-semibold text-gray-07 pl-2 py-3.5 whitespace-nowrap align-middle';
  const CHECKBOX_HEADER_CLASS = 'w-px pl-5 pr-0 py-3.5 align-middle';
  const INDEXER_HEADER_CLASS = `${HEADER_CLASS} min-w-30`;
  const STAGE_HEADER_CLASS = `${HEADER_CLASS} min-w-46`;
  const STATUS_HEADER_CLASS =
    'min-w-30 text-center text-xs/5 font-semibold text-gray-07 px-6 py-3.5 whitespace-nowrap align-middle';
  const ACTIONS_HEADER_CLASS = `${HEADER_CLASS} min-w-38`;
</script>

<template>
  <div class="w-full">
    <div
      v-if="hasResults || hasQuery"
      class="mb-6 flex w-full flex-col gap-6"
    >
      <div class="flex flex-wrap items-center gap-4">
        <ColoredButton
          text="Lista Mestra RQ"
          :icon="RegistrationPaperIcon"
          icon-alt="Lista Mestra RQ"
          variant="active"
          class="w-fit"
          :disabled="disablePendingActions"
          @click="emit('master-list')"
        />
        <ColoredButton
          text="Relatório"
          :icon="MenuFriesLeftIcon"
          icon-alt="Relatório"
          variant="active"
          class="w-fit"
          :disabled="disablePendingActions"
          @click="emit('report')"
        />
      </div>

      <!-- `independent-search-field` is not optional here: without it the component writes to the
           module ref of `useDocumentSearchTypeStore`, which survives navigation and would change
           the search field of every other listing in the session.
           `clear-search-on-field-change` is false so the term survives a field change: the
           component then re-emits `search` with it, and that is what re-runs the search on the
           field just chosen. -->
      <SearchToolbar>
        <template #search>
          <FieldSearchBar
            v-model:search-field-id="searchFieldId"
            :search-fields="QUALITY_RECORD_SEARCH_FIELDS"
            :initial-search-term="props.searchTerm ?? ''"
            independent-search-field
            :clear-search-on-field-change="false"
            :disabled="disablePendingActions"
            @search="(query) => emit('search', query)"
          />
        </template>
        <template
          v-if="hasResults || shouldShowNoResults"
          #quantity
        >
          <SelectInput
            :model-value="itemsPerPage"
            :options="QUANTITY_OPTIONS"
            class="cursor-pointer"
            @update:model-value="(quantity) => emit('quantity-changed', quantity)"
          />
        </template>
      </SearchToolbar>
    </div>

    <div
      v-if="loading && !searchTerm"
      class="w-full"
    >
      <TableLoading :row-size="10" />
    </div>

    <div v-else-if="hasResults || shouldShowNoResults">
      <DefaultTable class="divide-y divide-gray-02 min-w-full">
        <thead class="bg-white-01 divide-y divide-gray-02">
          <tr>
            <td
              :colspan="6"
              class="py-3 pl-6 pr-4"
            >
              <div class="flex items-center gap-4">
                <span
                  v-if="title"
                  class="text-sm/5 font-semibold text-gray-07"
                  data-testid="quality-records-title"
                >
                  {{ title }}
                </span>
                <QualityRecordsActionButtons
                  class="ml-auto"
                  :order-options="orderOptions"
                  :is-filter-active="isFilterActive"
                  :has-selected-records="hasSelectedRecords"
                  :can-delete="false"
                  :sort-and-filter-disabled="filterDisabled"
                  :sort-disabled="disablePendingActions && !enableSort"
                  :sort-source="sortSource"
                  @update:sort="(sort, order) => emit('update:sort', sort, order)"
                  @update:filter="handleFilter"
                  @filter-clicked="handleFilterClicked"
                />
              </div>
            </td>
          </tr>
          <tr v-if="!shouldShowNoResults">
            <TableHeader
              text="Registros Gerados"
              :checkbox="true"
              :checked="allSelected"
              :th-checkbox-class="CHECKBOX_HEADER_CLASS"
              :th-class="CODE_HEADER_CLASS"
              @toggle-all="handleToggleAll"
            />
            <TableHeader
              text="Indexador"
              :th-class="INDEXER_HEADER_CLASS"
            />
            <TableHeader
              text="Etapa"
              :th-class="STAGE_HEADER_CLASS"
            />
            <TableHeader
              text="Status"
              :th-class="STATUS_HEADER_CLASS"
            />
            <TableHeader
              text="Ações"
              :th-class="ACTIONS_HEADER_CLASS"
            />
          </tr>
        </thead>
        <tbody class="bg-white-01 divide-y divide-gray-02 border-t border-gray-02">
          <tr v-if="shouldShowNoResults">
            <td :colspan="6">
              <SearchNoResults />
            </td>
          </tr>
          <template
            v-for="record in documents"
            v-else
            :key="record.id"
          >
            <tr
              class="table-row-hover h-12 border-t border-gray-02 transition-colors duration-300 ease-out"
              data-testid="quality-record-row"
            >
              <td class="w-px pl-5 pr-0 py-2">
                <div class="flex items-center">
                  <Checkbox
                    :id="record.id"
                    :checked="isRecordSelected(record.id)"
                    @toggle="handleToggleSelection(record.id)"
                  />
                </div>
              </td>
              <!-- `max-w-0` lets the leftover width bound the cell: without it the auto layout grows
                   the column to a long IF or OS name and pushes the fixed columns off the card. -->
              <td class="max-w-0 pl-2 py-2 pr-0">
                <div class="flex min-w-0 items-center gap-2">
                  <RecordTypeBadge :record-type-name="record.recordTypeName" />
                  <div
                    class="min-w-0 truncate text-sm text-gray-07"
                    :title="formatRecordName(record)"
                    data-testid="quality-record-code"
                  >
                    {{ formatRecordName(record) }}
                  </div>
                </div>
              </td>

              <td class="pl-6 py-2 text-sm text-gray-07 text-left">
                {{ record.indexing }}
              </td>

              <td class="pl-6 py-2">
                <div class="flex items-center">
                  <span
                    :class="QUALITY_RECORD_STAGE_BADGE_CLASS"
                    data-testid="quality-record-stage-badge"
                  >
                    {{ record.stageName }}
                  </span>
                </div>
              </td>

              <td class="px-6 py-2">
                <div
                  class="flex items-center justify-center"
                  data-testid="quality-record-status-cell"
                >
                  <DocumentRevisionStatusDot
                    v-if="showsStatusDot(record)"
                    :status="record.status"
                    :label="getStatusTooltip(record)"
                    :color-classes="getQualityRecordStatusDotColorClass(record.status)"
                    focusable
                  />
                </div>
              </td>
              <td class="px-6 py-2 text-sm text-gray-07">
                <QualityRecordRowActions
                  :revision="record"
                  @refresh="emit('refresh')"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </DefaultTable>

      <div
        v-if="hasResults"
        class="mt-3"
      >
        <Pagination
          :total-items="totalItems"
          :current-page="currentPage"
          :items-per-page="itemsPerPage"
          :entity-name="paginationEntity"
          @page-changed="(page) => emit('page-changed', page)"
        />
      </div>
    </div>
    <LoadErrorState
      v-else-if="error"
      data-testid="quality-records-error"
      :title="GENERATED_RECORDS_LOAD_ERROR_TITLE"
      @retry="emit('retry')"
    />
    <NoDocuments
      v-else-if="shouldShowEmptyState"
      :title="emptyTitle"
      :icon="NoFilesIcon"
    />
  </div>
</template>
